/**
 * Downloads an image the browser was given a link to and hands back the base64
 * payload the identity `picture` PATCH expects.
 *
 * The download happens in the browser, so the image host has to allow
 * cross-origin reads. Hosts that don't (most "share link" pages, and S3 buckets
 * without a CORS policy) fail with a network error that carries no detail — the
 * message here says as much rather than pretending to know why.
 */

/** Oversized photos are rejected client-side rather than pushed at the API. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024

const EXTENSION_CONTENT_TYPES: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    heic: 'image/heic',
    heif: 'image/heif',
}

/** Magic-number sniff, for hosts that serve images as `application/octet-stream`. */
const sniffContentType = (bytes: Uint8Array): string | undefined => {
    if (bytes.length < 12) return undefined
    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg'
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
        return 'image/png'
    }
    if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) return 'image/gif'
    if (bytes[0] === 0x42 && bytes[1] === 0x4d) return 'image/bmp'
    const ascii = (offset: number, length: number) =>
        String.fromCharCode(...Array.from(bytes.slice(offset, offset + length)))
    if (ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') return 'image/webp'
    if (ascii(4, 4) === 'ftyp' && /^(heic|heix|hevc|mif1|msf1)$/.test(ascii(8, 4))) {
        return 'image/heic'
    }
    return undefined
}

/** Last-resort guess from the URL's file extension. */
export const contentTypeFromUrl = (url: string): string | undefined => {
    let pathname: string
    try {
        pathname = new URL(url).pathname
    } catch {
        pathname = url
    }
    const extension = pathname.split('.').pop()?.toLowerCase()
    return extension ? EXTENSION_CONTENT_TYPES[extension] : undefined
}

const toBase64 = (bytes: Uint8Array): string => {
    // Chunked so a multi-megabyte photo can't blow the argument limit on apply.
    const CHUNK = 0x8000
    let binary = ''
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + CHUNK)))
    }
    return btoa(binary)
}

export interface RemoteImage {
    /** Raw base64, with no `data:` prefix — what the PATCH body wants. */
    base64: string
    contentType: string
    bytes: number
}

const prettyBytes = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)}MB`

/**
 * Fetches `url` and returns it as base64. Throws with a message written to be
 * shown to the user verbatim.
 */
export const fetchRemoteImage = async (
    url: string,
    signal?: AbortSignal
): Promise<RemoteImage> => {
    let response: Response
    try {
        response = await fetch(url, { signal, credentials: 'omit', redirect: 'follow' })
    } catch (error: any) {
        if (error?.name === 'AbortError') throw error
        throw new Error(
            'could not be downloaded — the image host has to allow cross-origin requests'
        )
    }

    if (!response.ok) {
        throw new Error(`could not be downloaded (HTTP ${response.status} ${response.statusText})`)
    }

    const buffer = await response.arrayBuffer()
    if (buffer.byteLength === 0) throw new Error('downloaded as an empty file')
    if (buffer.byteLength > MAX_IMAGE_BYTES) {
        throw new Error(
            `is ${prettyBytes(buffer.byteLength)} — larger than the ${prettyBytes(MAX_IMAGE_BYTES)} limit`
        )
    }

    const bytes = new Uint8Array(buffer)
    const header = (response.headers.get('content-type') || '').split(';')[0].trim().toLowerCase()
    const contentType =
        (header.startsWith('image/') ? header : undefined) ??
        sniffContentType(bytes) ??
        contentTypeFromUrl(url)

    if (!contentType) {
        throw new Error(
            `returned ${header || 'an unrecognized file type'} rather than an image — check the link points straight at the file`
        )
    }

    return { base64: toBase64(bytes), contentType, bytes: buffer.byteLength }
}
